import { Controller } from "@hotwired/stimulus"
import { fetchLiveInput } from "../lib/cloudflare_live.js"

// Cloudflare status and playback. Confirmed state is exposed as data-live on
// the element so the surrounding status line and caption switch with CSS.
export default class extends Controller {
  static targets = ["mount", "template", "status", "notice", "placeholder", "placeholderTemplate", "announcement"]
  static values = { baseUrl: String, inputId: String }

  connect() {
    this.stopped = false
    this.videoId = null
    this.retryPlayback = false
    this.failures = 0
    this.restorePlaceholder()
    this.refresh()
  }

  disconnect() {
    this.stop()
  }

  stop() {
    this.stopped = true
    clearTimeout(this.timer)
    clearTimeout(this.requestTimeout)
    this.request?.abort()
    this.request = null
    delete this.element.dataset.live
    this.announcementTarget.textContent = ""
    this.clearPlayer()
    this.placeholderTarget.replaceChildren()
    this.placeholderTarget.hidden = false
  }

  async refresh() {
    if (this.stopped || this.request) return
    clearTimeout(this.timer)
    const request = new AbortController()
    this.request = request
    this.requestTimeout = setTimeout(() => request.abort(), 8_000)
    try {
      const state = await fetchLiveInput(this.baseUrlValue, this.inputIdValue, request.signal)
      if (this.stopped || this.request !== request) return
      this.failures = 0
      this.noticeTargets.forEach(target => { target.hidden = true })
      const previousLive = this.element.dataset.live
      this.element.dataset.live = String(state.live)
      if (state.live && previousLive !== "true") {
        this.announcementTarget.textContent = "The broadcast is live."
      } else if (!state.live && previousLive === "true") {
        this.announcementTarget.textContent = "The live broadcast has ended."
      }
      this.placeholderTarget.hidden = state.live
      if (state.live) {
        if (state.videoUID !== this.videoId || this.retryPlayback) this.showPlayer(state.videoUID)
      } else {
        this.restorePlaceholder()
        this.clearPlayer()
        this.statusTargets.forEach(target => {
          target.textContent = "Stay tuned. Playback will start automatically when the stream is available."
        })
      }
    } catch (error) {
      if (this.stopped || this.request !== request) return
      // Unknown is not offline: leave confirmed playback and status intact.
      this.failures++
      this.noticeTargets.forEach(target => {
        target.textContent = "Unable to check live status. Retrying automatically…"
        target.hidden = false
      })
    } finally {
      if (this.request === request) {
        clearTimeout(this.requestTimeout)
        this.request = null
        this.timer = setTimeout(() => this.refresh(), Math.min(60_000, 10_000 * 2 ** this.failures))
      }
    }
  }

  playbackFailed() {
    this.retryPlayback = true
    this.noticeTargets.forEach(target => {
      target.textContent = "Playback interrupted. Reconnecting automatically…"
      target.hidden = false
    })
  }

  showPlayer(videoId) {
    // Remove the fallback player so hidden or late-loading replay audio stops.
    this.placeholderTarget.replaceChildren()
    const fragment = this.templateTarget.content.cloneNode(true)
    const player = fragment.querySelector("media-player")
    const source = `${this.baseUrlValue}/${videoId}/manifest/video.m3u8`
    player.setAttribute("src", source)
    player.setAttribute("data-video-player-source-value", source)
    this.mountTarget.replaceChildren(fragment)
    this.videoId = videoId
    this.retryPlayback = false
  }

  restorePlaceholder() {
    if (!this.placeholderTarget.childElementCount) {
      this.placeholderTarget.replaceChildren(this.placeholderTemplateTarget.content.cloneNode(true))
    }
  }

  clearPlayer() {
    this.mountTarget.replaceChildren()
    this.videoId = null
    this.retryPlayback = false
  }
}
